import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Check, Tag as TagIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import type { TagRefType, TagScopeType } from '@aeris/contracts'
import {
  Badge,
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Spinner,
} from '@aeris/ui'

import { fetchTags } from '../lib/api.ts'

/**
 * O seletor de marcadores — F088.
 *
 * Um só para as duas famílias: `scope` decide quais aparecem, e a chave
 * estrangeira composta da migração 0049 garante que nenhum marcador de
 * conversa acabe colada num cadastro mesmo que alguém escreva a requisição à
 * mão.
 *
 * **Ele não cria marcador.** Criar é `tag.manage`, e é uma decisão sobre o
 * vocabulário da loja inteira — uma caixa "digite para criar" no meio de um
 * atendimento é como um cadastro ganha "Atacado", "atacado" e "ATACADO" na
 * primeira semana. Quando não há nenhuma, o seletor diz onde criá-las em vez
 * de oferecer um campo vazio.
 *
 * **Manda o conjunto INTEIRO a cada troca**, não "adicione esta". Ver
 * `SetTagsRequest`: um seletor de marcadores é um conjunto, e quem edita pensa
 * no resultado.
 */

export type TagPickerProps = {
  scope: TagScopeType
  /** Os ids marcados agora. */
  value: readonly string[]
  onChange: (tagIds: string[]) => void
  disabled?: boolean
  /** Como o botão se chama quando nada está marcado. */
  emptyLabel?: string
  /**
   * Só o ícone, com o rótulo em `sr-only` — para o cabeçalho da conversa, onde
   * cada palavra a mais é largura tirada do nome de quem está falando.
   */
  compact?: boolean
  className?: string
}

export const TagPicker = ({
  scope,
  value,
  onChange,
  disabled = false,
  emptyLabel = 'Marcadores',
  compact = false,
  className,
}: TagPickerProps): ReactNode => {
  const tags = useQuery({
    queryKey: ['tags', scope],
    queryFn: async ({ signal }) => await fetchTags(scope, 'active', signal),
    // O vocabulário da loja não muda no meio de um atendimento. Sem isso, cada
    // abertura do seletor seria uma requisição.
    staleTime: 5 * 60_000,
  })

  const available = tags.data ?? []
  const selected = new Set(value)

  const toggle = (id: string): void => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange([...next])
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          className={cn('gap-1.5', className)}
        >
          <TagIcon aria-hidden="true" className="h-4 w-4" />
          <span className={compact ? 'sr-only' : undefined}>
            {selected.size === 0
              ? emptyLabel
              : `${String(selected.size)} marcador${selected.size === 1 ? '' : 's'}`}
          </span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
        {tags.isPending ? (
          <div className="flex items-center gap-2 px-2 py-1.5 text-(length:--text-sm) text-(--text-muted)">
            <Spinner label="Carregando marcadores" />
            Carregando…
          </div>
        ) : null}

        {!tags.isPending && available.length === 0 ? (
          <div className="px-2 py-1.5 text-(length:--text-sm) text-(--text-muted)">
            Nenhum marcador criado ainda.
          </div>
        ) : null}

        {available.map((tag) => (
          <DropdownMenuItem
            key={tag.id}
            /*
             * `preventDefault` mantém o menu aberto: marcar três marcadores é
             * uma tarefa só, e reabrir o menu entre cada uma seria três vezes
             * o trabalho.
             */
            onSelect={(event) => {
              event.preventDefault()
              toggle(tag.id)
            }}
          >
            <Check
              aria-hidden="true"
              className={cn(
                'h-4 w-4 shrink-0',
                selected.has(tag.id) ? 'opacity-100' : 'opacity-0'
              )}
            />
            <Badge tone={tag.tone}>{tag.name}</Badge>
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/marcadores" className="text-(--text-muted)">
            Gerenciar marcadores
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Os marcadores desenhados, sem nada para clicar. */
export const TagBadges = ({
  tags,
  className,
}: {
  tags: readonly TagRefType[]
  className?: string
}): ReactNode => {
  if (tags.length === 0) return null
  return (
    <span className={cn('flex flex-wrap items-center gap-1', className)}>
      {tags.map((tag) => (
        <Badge key={tag.id} tone={tag.tone}>
          {tag.name}
        </Badge>
      ))}
    </span>
  )
}
